import { Component, Inject, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  ImageCroppedEvent,
  ImageCropperComponent as NgxImageCropper,
  LoadedImage,
} from 'ngx-image-cropper';
import { TranslateService } from '@ngx-translate/core';

interface CropperDialogData {
  imageFile: File;
  aspectRatio: number;
  maintainAspectRatio: boolean;
  cropperMinWidth?: number;
  cropperMinHeight?: number;
}

@Component({
  selector: 'app-image-cropper',
  template: `
    <div class="cropper-dialog">
      <h2 mat-dialog-title class="dialog-title">
        {{ 'Crop Cover Image' | translate }}
      </h2>

      <div class="cropper-content">
        <div class="cropper-instructions">
          <p class="instruction-text">
            {{
              'Adjust the crop area to fit your clinic cover image. The recommended aspect ratio is 4:3.'
                | translate
            }}
          </p>
        </div>

        <div class="cropper-container">
          <image-cropper
            [imageFile]="data.imageFile"
            [maintainAspectRatio]="true"
            [aspectRatio]="4 / 3"
            [cropperMinWidth]="data.cropperMinWidth || 200"
            [cropperMinHeight]="data.cropperMinHeight || 150"
            [resizeToWidth]="1600"
            [resizeToHeight]="1200"
            [canvasRotation]="canvasRotation"
            [transform]="transform"
            [format]="getOriginalFormat()"
            (imageCropped)="imageCropped($event)"
          >
          </image-cropper>
        </div>

        <div class="cropper-controls">
          <div class="control-icons">
            <mat-icon
              class="action-icon"
              (click)="rotateLeft()"
              matTooltip="Rotate Left"
            >
              rotate_left
            </mat-icon>

            <mat-icon
              class="action-icon"
              (click)="rotateRight()"
              matTooltip="Rotate Right"
            >
              rotate_right
            </mat-icon>

            <mat-icon
              class="action-icon"
              (click)="flipHorizontal()"
              matTooltip="Flip Horizontal"
            >
              swap_horiz
            </mat-icon>

            <mat-icon
              class="action-icon"
              (click)="flipVertical()"
              matTooltip="Flip Vertical"
            >
              swap_vert
            </mat-icon>

            <mat-icon
              class="action-icon"
              (click)="resetImage()"
              matTooltip="Reset Image"
            >
              refresh
            </mat-icon>
          </div>
        </div>
      </div>

      <div class="dialog-actions">
        <app-button
          [size]="'md'"
          buttonType="light"
          [outlined]="true"
          (click)="cancel()"
          [label]="'Cancel' | translate"
        >
        </app-button>

        <app-button
          [size]="'md'"
          variant="primary"
          (click)="cropAndSave()"
          [isDisabled]="!croppedImage"
          buttonType="primary"
          [label]="'Crop & Save' | translate"
        >
        </app-button>
      </div>
    </div>
  `,
  styles: [
    `
      .cropper-dialog {
        min-width: 650px;
        max-width: 90vw;
        max-height: 90vh;
        display: flex;
        flex-direction: column;
        background: white;
      }

      .dialog-title {
        margin: 0;
        padding: 24px 24px 16px 24px;
        font-size: 25px;
        font-weight: 500;
      }

      .cropper-content {
        flex: 1;
        padding: 24px;
        overflow-y: auto;
      }

      .cropper-instructions {
        padding-bottom: 16px;
      }

      .instruction-text {
        margin: 0;
        color: #666;
        font-size: 14px;
        line-height: 1.5;
      }

      .cropper-container {
        flex: 1;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      ::ng-deep .cropper-container image-cropper {
        max-width: 100%;
        max-height: 400px;
      }

      .cropper-controls {
        padding: 20px 0;
      }

      .control-icons {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 24px;
        flex-wrap: wrap;
      }

      .action-icon {
        cursor: pointer;
        color: #666;
        font-size: 24px;
        width: 48px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        user-select: none;
      }

      .action-icon:hover {
        color:   var(--color-primary);;
      }

      .dialog-actions {
        padding: 16px 24px 24px 24px;
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        flex-shrink: 0;
      }
    `,
  ],
  standalone: false
})
export class ImageCropperComponent {
  @ViewChild(NgxImageCropper) imageCropper!: NgxImageCropper;

  croppedImage: string = '';
  croppedFile: File | null = null;
  canvasRotation = 0;
  rotation = 0;
  scale = 1;
  transform: any = {};

  constructor(
    public dialogRef: MatDialogRef<ImageCropperComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CropperDialogData,
    private translateService: TranslateService
  ) { }

  getOriginalFormat(): 'png' | 'jpeg' | 'webp' {
    const mimeType = this.data.imageFile.type;
    if (mimeType.includes('jpeg') || mimeType.includes('jpg')) {
      return 'jpeg';
    } else if (mimeType.includes('png')) {
      return 'png';
    } else if (mimeType.includes('webp')) {
      return 'webp';
    }
    return 'jpeg';
  }

  getOutputMimeType(): string {
    const format = this.getOriginalFormat();
    switch (format) {
      case 'png':
        return 'image/png';
      case 'webp':
        return 'image/webp';
      case 'jpeg':
      default:
        return 'image/jpeg';
    }
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.objectUrl || event.base64 || '';
    if (event.blob) {
      const originalName = this.data.imageFile.name;
      const mimeType = this.getOutputMimeType();

      this.croppedFile = new File([event.blob], originalName, {
        type: mimeType,
        lastModified: Date.now(),
      });
    }
  }

  rotateLeft() {
    this.canvasRotation--;
    this.flipAfterRotate();
  }

  rotateRight() {
    this.canvasRotation++;
    this.flipAfterRotate();
  }

  flipHorizontal() {
    this.transform = {
      ...this.transform,
      flipH: !this.transform.flipH,
    };
  }

  flipVertical() {
    this.transform = {
      ...this.transform,
      flipV: !this.transform.flipV,
    };
  }

  resetImage() {
    this.canvasRotation = 0;
    this.rotation = 0;
    this.scale = 1;
    this.transform = {};
  }

  private flipAfterRotate() {
    const flippedH = this.transform.flipH;
    const flippedV = this.transform.flipV;
    this.transform = {
      ...this.transform,
      flipH: flippedV,
      flipV: flippedH,
    };
  }

  cropAndSave() {
    if (this.croppedFile && this.croppedImage) {
      this.dialogRef.close({
        croppedFile: this.croppedFile,
        croppedImageUrl: this.croppedImage,
      });
    }
  }

  cancel() {
    this.dialogRef.close();
  }
}
